import { useEffect, useReducer, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { ApiResponseResult } from '@/api/client'
import { fetchOrderFeed } from '@/api/orders/api'
import type { OrderImageItem, OrderImagesFeed } from '@/api/orders/types'
import { mergeOrderItems, ORDER_FEED_LIMIT } from '@/features/orders/ordersFromFeed'

export const ORDERS_REFRESH_INTERVAL = 60_000

interface FeedRequestState {
  readonly cursor: string | null
  readonly etag: string | null
  readonly lastModified: string | null
}

interface OrderFeedState {
  readonly items: readonly OrderImageItem[]
}

interface OrderFeedAction {
  readonly type: 'response'
  readonly response: ApiResponseResult<OrderImagesFeed>
}

function orderFeedReducer(state: OrderFeedState, action: OrderFeedAction): OrderFeedState {
  if (action.response.data === undefined) return state

  return {
    items: mergeOrderItems(state.items, action.response.data.items),
  }
}

export function useOrders() {
  const [state, dispatch] = useReducer(orderFeedReducer, { items: [] })
  const requestState = useRef<FeedRequestState>({ cursor: null, etag: null, lastModified: null })
  const appliedResponse = useRef<ApiResponseResult<OrderImagesFeed> | null>(null)

  const query = useQuery({
    queryKey: ['orders-feed', ORDER_FEED_LIMIT],
    queryFn: ({ signal }) =>
      fetchOrderFeed({
        orderCount: ORDER_FEED_LIMIT,
        since: requestState.current.cursor,
        etag: requestState.current.etag,
        lastModified: requestState.current.lastModified,
        signal,
      }),
    refetchInterval: ORDERS_REFRESH_INTERVAL,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    staleTime: 0,
  })

  useEffect(() => {
    const response = query.data
    if (response === undefined || response === appliedResponse.current) return
    appliedResponse.current = response

    const etag = response.headers.get('ETag') ?? requestState.current.etag
    const lastModified = response.headers.get('Last-Modified') ?? requestState.current.lastModified

    if (response.status === 304 || response.data === undefined) {
      requestState.current = {
        cursor: requestState.current.cursor,
        etag,
        lastModified,
      }
      return
    }

    requestState.current = {
      cursor: response.data.nextCursor,
      etag,
      lastModified,
    }
    dispatch({ type: 'response', response })
  }, [query.data])

  return {
    data: state.items,
    dataUpdatedAt: query.dataUpdatedAt,
    error: query.error,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    refetch: query.refetch,
  }
}
